import React, { useCallback, useMemo, useState } from "react";
import { ArrayField, useFieldArray } from "react-hook-form";
import { cloneDeep, get } from "lodash";
import { Section } from "../../graphql/types";
import useTranslations from "../../hooks/useTranslations";
import { FormStepProps } from "../../screens/ReviewFormScreen";
import { DeleteIcon, PlusIcon } from "../icons";
import { Button as WombatButton, Input, Textarea } from "../wombat-ui";
import { QuestionFormInput } from "./QuestionWithOptionsForm";
import QuestionForm from "./QuestionForm";
import useConfirm from "../utils/confirm/useConfirm";
import AddTemplateElementButton from "./AddTemplateElementButton";
import { IconButton, MenuButton, Button } from "@chakra-ui/react";

export type SectionFormInput = Omit<
  Section,
  "id" | "__typename"
>;

export const SectionForm: React.FC<
  Omit<FormStepProps, "onNext" | "formState"> & {
    defaultValue: Partial<ArrayField<SectionFormInput, "id">>;
    index: number;
    onRemove: (index: number) => void;
    handleFocus: (sectionIndex: number, questionIndex: number) => void;
    errors;
    focusedIndex: string;
    onBlur: () => void;
    sectionDisplayIndex: number;
    name: string;
  }
> = React.memo(
  ({
    sectionDisplayIndex,
    register,
    unregister,
    setValue,
    getValues,
    index,
    control,
    trigger,
    errors,
    defaultValue,
    onRemove,
    handleFocus,
    focusedIndex,
    onBlur,
    reset,
    name,
  }) => {
    const t = useTranslations();

    const confirm = useConfirm();
    const [showSection, setShowSection] = useState(true);

    const { fields, append, insert } = useFieldArray<QuestionFormInput, "key">({
      control,
      name: `${name}.questionsAttributes`,
      keyName: "key",
    });

    const _onRemove = useCallback(() => {
      confirm({
        title: t("templates.deleteSection"),
        message: t("templates.deleteSectionWarning"),
        buttons: [
          { text: t("forms.actions.cancel") },
          {
            colorScheme: "red",
            id: "delete-section",
            text: t("forms.actions.delete"),
            onClick: () => {
              onBlur();
              onRemove(index);
            },
          },
        ],
      });
    }, [index, onRemove]);

    const handleRemoveQuestion = useCallback(
      (questionIndex) => {
        confirm({
          title: t("reviews.deleteQuestion"),
          message: t("templates.deleteQuestionWarning"),
          buttons: [
            { text: t("forms.actions.cancel") },
            {
              colorScheme: "red",
              id: "delete-question",
              text: t("forms.actions.delete"),
              onClick: () => {
                onBlur();

                // clone it to make mutating the value easier
                const values = cloneDeep(getValues());

                get(values, name).questionsAttributes.splice(questionIndex, 1);
                reset(values);
              },
            },
          ],
        });
      },
      [name]
    );

    const handleQuestionFocus = useCallback(
      (questionIndex: number) => {
        handleFocus(index, questionIndex);
      },
      [handleFocus, index]
    );


    return (
      <div
        id={`${name}.sectionBlock`}
        className={`rounded shadow-md bg-light-0 ${
          index === 0 ? "mt-3" : "mt-6"
        }`}
      >
        <div className="flex items-center justify-between w-full px-4 py-3 rounded-t shadow-md bg-secondary-600">
          <span
            className="text-lg font-semibold text-white"
            id={`${name}.sectionHeader`}
          >
            {t("reviews.sectionHeader", {
              sectionIndex: sectionDisplayIndex + 1,
              questionsCount: fields.length,
            })}
          </span>
          <WombatButton
            id={`${name}.deleteSection`}
            onClick={() => _onRemove()}
            variant="subtle"
            trailingIcon={DeleteIcon}
          >
            {t("templates.deleteSection")}
          </WombatButton>
        </div>
        <div className="px-6 py-6">
          <div className="flex justify-between pb-4">
            <div className="flex-grow pr-8">
              <Input
                id={`${name}.title`}
                label={t("templates.sectionTitleLabel") as string}
                placeholder={t("templates.sectionTitlePlaceholder") as string}
                name={`${name}.title`}
                ref={register({
                  required: t("templates.sectionTitleRequired") as string,
                })}
                error={errors?.title}
                defaultValue={defaultValue.title}
              />
            </div>
          </div>
          <div className="pb-4">
            <Textarea
              id={`${name}.description`}
              label={t("templates.sectionDescriptionLabel") as string}
              placeholder={
                t("templates.sectionDescriptionPlaceholder") as string
              }
              name={`${name}.description`}
              ref={register()}
              rows={2}
              defaultValue={defaultValue.description}
            />
          </div>
          {showSection ? (
            <>
              <div className="flex items-center">
                <WombatButton
                  variant="link"
                  scale="inline"
                  className="mt-0.5 text-sm"
                  onClick={() => setShowSection(!showSection)}
                >
                  {t("templates.hideContent")}
                </WombatButton>
              </div>
              {fields.map((field, questionIndex) => {
                const focused =
                  focusedIndex === `${index}-${questionIndex}`;
                return (
                  <React.Fragment key={questionIndex}>
                    <div className="relative flex justify-center sm:mx-8 group">
                      <div className="absolute w-full h-px inset-y-1/2 bg-light-100 group-hover:bg-light-500" />
                      <AddTemplateElementButton
                        insideSection
                        handleFocus={handleFocus}
                        setValue={setValue}
                        getValues={getValues}
                        fields={fields}
                        append={append}
                        insert={insert}
                        reset={reset}
                        name={name}
                        sectionIndex={index}
                        insertIndex={questionIndex}
                      >
                        <MenuButton
                          as={IconButton}
                          aria-label="Add form element"
                          icon={<PlusIcon className="w-4 h-4 text-dark-100" />}
                          data-test-id={`insert_question_${name}_${questionIndex}`}
                        />
                      </AddTemplateElementButton>
                    </div>
                    <div
                      key={field.key}
                      role={!focused ? "button" : undefined}
                      className={`py-3`}
                      id={`${name}.questionBar`}
                    >
                      <QuestionForm
                        index={questionIndex}
                        control={control}
                        register={register}
                        trigger={trigger}
                        unregister={unregister}
                        setValue={setValue}
                        getValues={getValues}
                        reset={reset}
                        defaultValue={field}
                        errors={
                          errors?.questionsAttributes?.[questionIndex]
                        }
                        onRemove={handleRemoveQuestion}
                        focused={focused}
                        onBlur={onBlur}
                        onFocus={handleQuestionFocus}
                        name={`${name}.questionsAttributes[${questionIndex}]`}
                      />
                    </div>
                  </React.Fragment>
                );
              })}
              <AddTemplateElementButton
                insideSection
                handleFocus={handleFocus}
                setValue={setValue}
                getValues={getValues}
                fields={fields}
                append={append}
                insert={insert}
                reset={reset}
                name={name}
                sectionIndex={index}
                insertIndex={-1}
              >
                <MenuButton
                  as={Button}
                  aria-label="Add form element"
                  data-test-id={`${name}.addElementButton`}
                >
                  {t("templates.createElementInsideSection")}
                </MenuButton>
              </AddTemplateElementButton>
            </>
          ) : (
            <>
              <div className="flex items-center pt-0.5">
                <WombatButton
                  variant="link"
                  scale="inline"
                  className="text-sm"
                  onClick={() => setShowSection(!showSection)}
                >
                  {t("templates.showContent")}
                </WombatButton>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
);
